import React, {useEffect, useMemo} from 'react';
import {Text, TouchableOpacity, View} from 'react-native';
import Screen from '../../../../../shared/components/screen';
import {CustomScrollView} from '../../../../../shared/components/scrollView';
import {useStyles} from '../../../../../shared/hooks/styles';
import {useAccountLinker} from '../../../../../shared/hooks/useAccountLinkerHook/useAccountLinker';
import {Screens} from '../../../../../shared/routing';
import {LoadableContent} from '../../../../../shared/components/loadableContent';
import {LinkedAccountsListItem} from './listItem';
import {CounterpartyLinkedProfile} from '../../../../../shared/hooks/useAccountLinkerHook/Types';
import {NavigationHelpers, ParamListBase, useNavigation} from '@react-navigation/native';

import {useAppDispatch} from '../../../../../redux';
import {addNotification} from '../../../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../../../../shared/components/snackbar/model';

const AccountItem = (props: {
  onPressAccountItem: (linkedAccount: CounterpartyLinkedProfile) => void;
  screen: Screens;
  title: string;
  testID: string;
  linkedAccount: CounterpartyLinkedProfile;
}) => {
  const {onPressAccountItem, linkedAccount} = props;
  const styles = useStyles(() => ({
    container: {
      marginTop: 10,
      paddingHorizontal: 16,
      paddingBottom: 0,
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  }));

  return (
    <TouchableOpacity testID={`testID`} onPress={() => onPressAccountItem(linkedAccount)} accessibilityRole='link'>
      <View style={styles.container}>
        <LinkedAccountsListItem
          style={{
            marginBottom: 0,
          }}
          linkedAccount={linkedAccount}
        />
      </View>
    </TouchableOpacity>
  );
};

export function ProfileLinkedAccounts() {
  const {linkedAccounts, loadLinkedAccountsInfo, isLoading} = useAccountLinker();
  const nav = useNavigation() as unknown as NavigationHelpers<ParamListBase>;
  const dispatch = useAppDispatch();

  useEffect(() => {
    loadLinkedAccountsInfo();
  }, []);

  const nonActivatedStatuses = [5, 6, undefined];
  const needActivation = useMemo(() => {
    return linkedAccounts.some(
      (account) => account.cardNumber == null || nonActivatedStatuses.includes(account.cardStatusId),
    );
  }, [linkedAccounts]);

  useEffect(() => {
    if (needActivation)
      dispatch(
        addNotification({
          message: 'Активируйте карты связанных аккаунтов',
          type: SnackbarNotificationType.Warning,
        }),
      );
  }, [needActivation]);

  const handlePressAccountItem = (linkedAccount: CounterpartyLinkedProfile) => {
    nav.navigate(Screens.ProfileLinkedAccountsItem, {
      'linkedAccount-id': String(linkedAccount.id),
    });
  };
  const styles = useStyles(({theme}) => {
    return {
      container: {
        marginBottom: theme.screen.bottomOffset,
      },
      h2: {
        alignSelf: 'center',
        ...theme.fonts.h2,
      },
      h3: {
        alignSelf: 'center',
        ...theme.fonts.h3,
      },
      h4: {
        alignSelf: 'flex-start',
        ...theme.fonts.h4,
        margin: 5,
      },
      icon: {
        alignSelf: 'center',
        ...theme.fonts.h4,
        marginLeft: 5,
      },
      item: {
        marginTop: 21,
        paddingHorizontal: 24,
        paddingBottom: 32,
      },
      rightColor: {
        color: theme.color.labelText,
      },
    };
  });

  const parentsLinks = linkedAccounts.filter((accountInfo) => accountInfo.isCardHolderParent === true);
  const childrenLinks = linkedAccounts.filter((accountInfo) => accountInfo.isCardHolderParent === false);
  const presenceLinkingAccountRequests =
    linkedAccounts.length === 0 ? 'noLinkingAccountRequest' : 'linkingAccountRequestsPresent';
  const displayStatusLinkingAccountRequests = {
    noLinkingAccountRequest: (
      <>
        <Text testID='profile-label-id' style={styles.h4}>
          Нет связанных аккаунтов.
        </Text>
        <Text testID='profile-label-id' style={styles.h4}>
          Для подключения нажмите иконку "плюс" в верхнем правом углу экрана.
        </Text>
      </>
    ),
    linkingAccountRequestsPresent: (
      <>
        {parentsLinks.length !== 0 ? (
          <>
            <Text testID='profile-label-id' style={styles.h3}>
              Доступ предоставлен
            </Text>
            {parentsLinks.map((accountInfo, i) => {
              return (
                <AccountItem
                  key={i}
                  linkedAccount={accountInfo}
                  onPressAccountItem={handlePressAccountItem}
                  screen={Screens.ProfileLinkedAccountsItem}
                  testID={String(accountInfo.id)}
                  title={accountInfo.counterpartyFullName}
                />
              );
            })}
          </>
        ) : null}
        {childrenLinks.length !== 0 ? (
          <>
            <Text testID='profile-label-id' style={styles.h3}>
              Доступ предоставили
            </Text>
            {childrenLinks.map((accountInfo, i) => {
              return (
                <AccountItem
                  key={i}
                  linkedAccount={accountInfo}
                  onPressAccountItem={handlePressAccountItem}
                  screen={Screens.ProfileLinkedAccountsItem}
                  testID={String(accountInfo.id)}
                  title={accountInfo.counterpartyFullName}
                />
              );
            })}
          </>
        ) : null}
      </>
    ),
  };

  return (
    <Screen>
      <LoadableContent loading={isLoading}>
        <CustomScrollView contentInsetAdjustmentBehavior='automatic'>
          <View style={styles.container}>{displayStatusLinkingAccountRequests[presenceLinkingAccountRequests]}</View>
        </CustomScrollView>
      </LoadableContent>
    </Screen>
  );
}
